import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MuiSelect from "@mui/material/Select";
import { gql } from "@apollo/client";
import {
  YearRangeSelector,
  ConditionalRenderer,
  useLanguageTranslator,
} from "../../../components";
import { MinusIcon } from "../../../@lib/icons/minus";
import { PlusIcon } from "../../../@lib/icons/plus";
import { toastHandler } from "../../../utils/helpers";
import { toastTypesKeys } from "../../../utils/constants";
import { ToastContainer } from "react-toastify";
import { useState } from "react";
import { CountryCategoryFilter } from "./countryCategorySectionFilter";

const Select = styled(MuiSelect)(({ theme }) => ({
  borderRadius: 100,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[4],
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 200,
    },
  },
};

function Filters({
  setFilters,
  filters,
  my = 5,
  isCountry = false,
  submitClickHandler,
  loading = false,
  filtersData = {},
}: any) {
  const { translator } = useLanguageTranslator();

  const { data = {}, indicatorCategoriesData = {} } = filtersData;

  const handleChange = (event: any) => {
    setFilters({ ...filters, [event.target.name]: event.target.value });
  };

  const handleMultipleChange = (event: any) => {
    const {
      target: { value },
    } = event;
    setFilters({
      ...filters,
      category: typeof value === "string" ? value.split(",") : value,
    });
  };

  function handleClearFilters() {
    setFilters({
      country: "",
      startYear: "",
      endYear: "",
      theme: "",
      category: [],
    });
  }

  const totalWeight = filters?.category?.reduce(
    (sum: any, item: any) => sum + (item.weight || 0),
    0
  );

  const isFiltersNotApplied =
    !filters?.country ||
    !filters?.startYear ||
    !filters?.endYear ||
    !filters?.theme ||
    totalWeight > 100;

  return (
    <>
      <Container sx={{ my: my }}>
        <Stack direction="row" gap={2} alignItems="center">
          <Typography variant="body2" color="textSecondary">
            {translator(`Choose by`)}:
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>
                {translator("Country")}
                <span style={{ color: 'red', marginLeft: '4px' }} aria-hidden="true">*</span>
              </InputLabel>
              <Select
                label={translator("Country")}
                name="country"
                onChange={handleChange}
                value={filters?.country}
              >
                {data?.countryOptions?.map((op: any, i: any) => (
                  <MenuItem key={`${op.value}-${i}`} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 120, height: "47px" }}>
              <InputLabel
                sx={{ backgroundColor: "white" }}
                shrink={!!filters?.startYear && !!filters?.endYear}
              >
                {translator("Period")}
                <span style={{ color: 'red', marginLeft: '4px' }} aria-hidden="true">*</span>
              </InputLabel>
              <YearRangeSelector setFilters={setFilters} filters={filters} />
            </FormControl>

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>{translator("Theme")}</InputLabel>
              <Select
                sx={{ width: 150 }}
                label={translator("Theme")}
                name="theme"
                onChange={handleChange}
                value={filters?.theme}
              >
                {data?.themeOptions?.map((op: any, i: any) => (
                  <MenuItem key={`${op.value}-${i}`} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <ConditionalRenderer condition={!isCountry}>
              <FormControl sx={{ m: 1, minWidth: 120 }}>
                <InputLabel id="demo-multiple-checkbox-label">
                  {translator(`dashboardTableHead.Category`)}
                  <span style={{ color: 'red', marginLeft: '4px' }} aria-hidden="true">*</span>
                </InputLabel>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  sx={{ width: 150 }}
                  multiple
                  value={filters?.category}
                  onChange={handleMultipleChange}
                  input={<OutlinedInput label="Tag" />}
                  renderValue={(selected: any) => {
                    const matchedCategoryNames =
                      indicatorCategoriesData?.indicatorCategories
                        ?.filter((cat: any) =>
                          selected?.includes(cat?.id || null)
                        )
                        ?.map((cat: any) => cat?.name || "") || [];
                    return matchedCategoryNames?.join(", ");
                  }}
                  MenuProps={MenuProps}
                >
                  {indicatorCategoriesData?.indicatorCategories?.map(
                    (item: any) => (
                      <MenuItem key={item?.name} value={item?.id}>
                        <Checkbox
                          checked={filters?.category?.includes(item?.id)}
                        />
                        <ListItemText
                          primary={item?.name}
                          primaryTypographyProps={{ fontSize: "0.875rem" }}
                        />
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </ConditionalRenderer>
          </Stack>
        </Stack>
        <ConditionalRenderer condition={isCountry}>
          <CountryCategoryFilter
            // key={`key-${filters?.category}`}
            data={indicatorCategoriesData?.indicatorCategories || []}
            setFilterState={setFilters}
            filtersState={filters}
          />
        </ConditionalRenderer>
      </Container>
      <Stack direction="row" justifyContent="center" mt={6}>
        <Button
          fullWidth
          size="large"
          onClick={handleClearFilters}
          sx={{
            backgroundColor: "#E5E5EA",
            width: "230px",
            color: "#989C9F",
            fontSize: "14px",
            height: "36px",
          }}
        >
          {translator(`Discard`)}
        </Button>
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={submitClickHandler}
          disabled={loading || isFiltersNotApplied}
          sx={{
            backgroundColor: "#F6A000",
            width: "230px",
            margin: "0px 20px",
            fontSize: "14px",
            height: "36px",
          }}
        >
          <ConditionalRenderer condition={loading}>
            <Box sx={{ display: "flex" }}>
              <CircularProgress size={24} />
            </Box>
          </ConditionalRenderer>
          <ConditionalRenderer condition={!loading}>
            {translator(`Apply Changes`)}
          </ConditionalRenderer>
        </Button>
      </Stack>
    </>
  );
}
export { Filters };
